import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Loader2, ShieldAlert, UserCog, ArrowRight } from "lucide-react";
import api, { setAuthHeader } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const ERROR_COPY = {
  password_account_exists: "That email is already registered with a password. Sign in with your password first, then link Google from your profile.",
  google_sub_conflict: "This Google account is already linked to a different VenturaZ user.",
  email_not_verified: "Your Google email isn't verified yet. Please verify it with Google before trying again.",
  token_exchange_failed: "Couldn't complete Google sign-in. Please try again.",
  invalid_id_token: "Google returned an unverified token. Please try again.",
  invalid_nonce: "OAuth nonce mismatch. Please try signing in again.",
  invalid_issuer: "Unexpected token issuer. Please try again.",
  user_lookup_failed: "We couldn't create your account. Please try again or contact support.",
};

const ROLE_LABEL = {
  startup: "Founder (raising equity)",
  investor: "Investor",
  channel_partner: "Channel Partner",
  super_admin: "VenturaZ Admin",
  admin: "VenturaZ Admin",
  manager: "VenturaZ Manager",
  agent: "VenturaZ Agent",
  debt_head: "Debt Head",
  debt_manager: "Debt Manager",
  debt_agent: "Debt Agent",
  credit_analyst: "Credit Analyst",
};

/**
 * Landing page after our self-hosted Google OAuth callback.
 * Backend calls: 303 → /auth/google/return?token=...&redirect=...&new_user=0|1
 * If there's a google_error param, show the error message; otherwise store
 * the token via AuthContext and forward to the requested redirect.
 */
export default function GoogleAuthReturn() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [err, setErr] = useState(null);
  const [mismatch, setMismatch] = useState(null); // { requested, actual, profile }
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const params = new URLSearchParams(loc.search);
    const gerror = params.get("google_error");
    if (gerror) {
      const msg = ERROR_COPY[gerror] || `Google sign-in failed: ${gerror}`;
      setErr(msg);
      toast.error(msg);
      return;
    }
    // SEC-003: New flow — backend now redirects with an opaque one-time `code`.
    // Fall back to the legacy `token` query for backward compatibility during
    // the deploy window.
    const code = params.get("code");
    const legacyToken = params.get("token");
    const rememberMe = sessionStorage.getItem("venturaz_pending_remember_me") === "1";
    const intent = sessionStorage.getItem("venturaz_pending_login_intent") || null;
    sessionStorage.removeItem("venturaz_pending_remember_me");
    sessionStorage.removeItem("venturaz_google_role");
    sessionStorage.removeItem("venturaz_pending_login_intent");

    const finish = (token, redirect, isNew, requestedRole) => {
      setAuthHeader(token, { persist: rememberMe });
      api.get("/auth/me").then(({ data }) => {
        setUser(data);
        // Cross-signup detection — if the caller asked for role R but the
        // account already exists with role !== R, don't silently drop them
        // into the wrong dashboard. Show a popup with a one-click Request
        // Role Change flow. (Only fires for existing accounts.)
        const canSwitch = new Set(["startup", "investor"]);
        if (
          !isNew && requestedRole && data?.role && requestedRole !== data.role
          && canSwitch.has(requestedRole) && canSwitch.has(data.role)
        ) {
          setMismatch({ requested: requestedRole, actual: data.role, profile: data });
          return; // Stop routing — user must decide via the popup below.
        }
        toast.success(isNew ? `Welcome to VenturaZ, ${data.name || data.email}` : "Welcome back!");
        if (redirect && redirect !== "/") {
          nav(redirect, { replace: true });
          return;
        }
        const role = data?.role;

        // Admins/debt-team/partners: role always wins, ignore intent.
        if (["super_admin", "admin", "manager", "agent"].includes(role)) {
          nav("/admin/dashboard", { replace: true }); return;
        }
        if (["debt_head", "debt_manager", "debt_agent", "credit_analyst"].includes(role)) {
          nav("/admin/money/dashboard", { replace: true }); return;
        }
        if (role === "channel_partner") { nav("/partner/dashboard", { replace: true }); return; }

        // Debt-intent handoff — check if this normal user has any debt cases.
        if (intent === "debt") {
          api.get("/debt/cases", { params: { limit: 1 } })
            .then((r) => {
              const hasCases = (r.data?.items || []).length > 0;
              nav(hasCases ? "/my-debt-cases" : "/money?prefill=1", { replace: true });
            })
            .catch(() => nav("/money?prefill=1", { replace: true }));
          return;
        }
        if (intent === "investor" || role === "investor") {
          nav("/investor/dashboard", { replace: true }); return;
        }
        nav("/startup/dashboard", { replace: true });
      }).catch(() => {
        setAuthHeader(null);
        setErr("Couldn't load your profile after Google sign-in. Please try again.");
      });
    };

    if (code) {
      api.post("/auth/google/exchange", { code })
        .then(({ data }) => finish(data.token, data.redirect, !!data.new_user, data.requested_role || null))
        .catch((e) => {
          const msg = e?.response?.data?.detail || "Couldn't complete Google sign-in. Please try again.";
          setErr(msg);
          toast.error(msg);
        });
      return;
    }
    if (legacyToken) {
      // Legacy path retained temporarily; new backend uses code.
      const redirect = params.get("redirect") || "/";
      const isNew = params.get("new_user") === "1";
      finish(legacyToken, redirect, isNew, params.get("requested_role") || null);
      return;
    }
    setErr("Missing exchange code from Google callback");
  }, [loc.search, nav, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-transparent">
      <Toaster richColors position="top-right" />
      <div className="glass rounded-lg p-8 max-w-md w-full text-center" data-testid="google-auth-return">
        {err ? (
          <>
            <ShieldAlert size={44} className="text-rose-600 mx-auto mb-3" />
            <div className="font-heading text-xl text-ink">Google sign-in failed</div>
            <div className="text-sm text-ink-muted mt-2">{err}</div>
            <button
              onClick={() => nav("/login")}
              className="mt-6 px-4 py-2 bg-navy text-white rounded-md text-sm hover:bg-[#256E58]"
              data-testid="google-return-back"
            >Back to Sign In</button>
          </>
        ) : mismatch ? (
          <div className="text-left" data-testid="google-return-role-mismatch">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full inline-flex items-center justify-center bg-gold/20 text-[#8A6600] shrink-0">
                <UserCog size={20} />
              </div>
              <div>
                <div className="font-heading text-lg text-ink">You&apos;re already registered</div>
                <div className="text-xs text-ink-muted font-mono uppercase tracking-widest">Account type mismatch</div>
              </div>
            </div>
            <p className="text-sm text-ink-soft leading-relaxed mb-3">
              This Google account is signed in as
              <span className="font-heading text-ink mx-1">{ROLE_LABEL[mismatch.actual] || mismatch.actual}</span>.
              You tried to join as
              <span className="font-heading text-ink mx-1">{ROLE_LABEL[mismatch.requested] || mismatch.requested}</span>.
            </p>
            <p className="text-sm text-ink-soft leading-relaxed mb-5">
              We&apos;ve kept your existing account. If you actually meant to switch surfaces, you can request an admin role change — approvals typically take a few hours.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => nav(`/settings/role-change-request?to=${mismatch.requested}`, { replace: true })}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gold text-navy text-sm font-heading font-semibold hover:bg-yellow-400 transition-colors shadow-[0_6px_18px_rgba(212,175,55,0.28)]"
                data-testid="google-return-request-switch"
              >
                Request switch to {ROLE_LABEL[mismatch.requested] || mismatch.requested}
                <ArrowRight size={14} />
              </button>
              <button
                type="button"
                onClick={() => {
                  toast.success(`Welcome back, ${mismatch.profile?.name || mismatch.profile?.email}`);
                  const r = mismatch.actual;
                  if (r === "investor") nav("/investor/dashboard", { replace: true });
                  else nav("/startup/dashboard", { replace: true });
                }}
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 text-sm text-ink-soft hover:text-ink font-heading transition-colors"
                data-testid="google-return-keep-current"
              >
                Continue as {ROLE_LABEL[mismatch.actual] || mismatch.actual}
              </button>
            </div>
          </div>
        ) : (
          <>
            <Loader2 size={44} className="animate-spin text-navy mx-auto mb-3" />
            <div className="font-heading text-xl text-ink">Finishing sign-in…</div>
            <div className="text-sm text-ink-muted mt-2">Almost there.</div>
          </>
        )}
      </div>
    </div>
  );
}
